import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { MenuCheckboxItem } from '@creditornot/cb-menu';
import { Spinner } from '@creditornot/cb-progress';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { Divider } from 'common/components/cookbook/Divider';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

import { useProductFamiliesQuery } from './query';
import { SearchInput } from '../../Search/SearchInput';
import { TreeSelectOptions } from '../../TreeSelect/TreeSelectOptions';

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
`;

type ProductFamiliesFilterProps = {
  productFamilyIds: string[];
  onProductFamiliesChange: (ids: string[]) => void;
  noProductFamily?: boolean;
  onNoProductFamilyChange?: (value: boolean) => void;
};

export function ProductFamiliesFilter({
  productFamilyIds = [],
  onProductFamiliesChange,
  noProductFamily = false,
  onNoProductFamilyChange,
}: ProductFamiliesFilterProps) {
  const f = useFormatMessage();
  const [search, setSearch] = useState<string>('');

  const { data: productFamilies = [], isLoading, error } = useProductFamiliesQuery();

  const options = useMemo(() => {
    return productFamilies.map(({ id, name, parentId, status }) => {
      const label =
        status === 'DEPRECATED'
          ? `${name} (${f('catalog.product_family_status:DEPRECATED')})`
          : name;

      return {
        label,
        value: id,
        parentValue: parentId,
        checked: productFamilyIds.includes(id),
      };
    });
  }, [f, productFamilies, productFamilyIds]);

  function handleChange(values: string[]) {
    onProductFamiliesChange(values);
  }

  return (
    <Stack gap="regularMedium">
      <ApiErrorNotification error={error} />
      {isLoading && (
        <SpinnerContainer>
          <Spinner size="large" />
        </SpinnerContainer>
      )}
      {onNoProductFamilyChange && (
        <>
          <MenuCheckboxItem
            role="checkbox"
            checked={noProductFamily}
            onClick={() => onNoProductFamilyChange(!noProductFamily)}
          >
            {f('catalog.filter_products_without', { attribute: 'product family' })}
          </MenuCheckboxItem>
          <Divider style={{ margin: '8px -8px' }} />
        </>
      )}
      {options.length > 0 && (
        <div>
          <SearchInput aria-label={f('common.search')} value={search} onChange={setSearch} />
          <TreeSelectOptions
            onChange={({ selectedIds }) => handleChange(selectedIds)}
            options={options}
            search={search}
            multiselect
          />
        </div>
      )}
    </Stack>
  );
}
